import { useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Highlight from '@tiptap/extension-highlight';
import { Bold, Italic, List, ListOrdered, Link as LinkIcon, CheckSquare, IndentIncrease, IndentDecrease, Highlighter } from 'lucide-react';

const HIGHLIGHT_COLORS = [
  { name: 'Yellow', color: '#fef08a' },
  { name: 'Green', color: '#bbf7d0' },
  { name: 'Blue', color: '#bfdbfe' },
  { name: 'Pink', color: '#fbcfe8' },
  { name: 'Orange', color: '#fed7aa' },
  { name: 'Purple', color: '#ddd6fe' },
];

export const RichTextEditor = ({ value, onChange, placeholder, className = '' }) => {
  const isInternalUpdate = useRef(false);
  const onChangeRef = useRef(onChange);
  const [showHighlightPicker, setShowHighlightPicker] = useState(false);
  const pickerRef = useRef(null);

  // Keep onChange ref fresh to avoid stale closure in useEditor
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: false }),
      TaskList,
      TaskItem.configure({ nested: true }),
      Highlight.configure({ multicolor: true }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      isInternalUpdate.current = true;
      onChangeRef.current(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose max-w-none focus:outline-none min-h-[150px] p-3',
      },
    },
  });

  useEffect(() => {
    if (!editor) return;
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }
    const currentContent = editor.getHTML();
    if (value !== currentContent) {
      editor.commands.setContent(value || '', false);
    }
  }, [value, editor]);

  // Close picker on outside click
  useEffect(() => {
    const handler = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setShowHighlightPicker(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (!editor) {
    return null;
  }

  const btnClass = (active) =>
    `p-2 rounded hover:bg-white transition-colors ${active ? 'bg-white text-[#FF5A1F]' : 'text-[#71717A]'}`;

  return (
    <div className={`border border-[#E4E4E7] rounded-none ${className}`}>
      <div className="border-b border-[#E4E4E7] bg-[#FAFAFA] p-2 flex flex-wrap gap-1">
        <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} className={btnClass(editor.isActive('bold'))} title="Bold">
          <Bold size={16} strokeWidth={1.5} />
        </button>
        <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} className={btnClass(editor.isActive('italic'))} title="Italic">
          <Italic size={16} strokeWidth={1.5} />
        </button>
        <div className="w-px bg-[#E4E4E7] mx-1"></div>
        <button type="button" onClick={() => editor.chain().focus().toggleBulletList().run()} className={btnClass(editor.isActive('bulletList'))} title="Bullet List">
          <List size={16} strokeWidth={1.5} />
        </button>
        <button type="button" onClick={() => editor.chain().focus().toggleOrderedList().run()} className={btnClass(editor.isActive('orderedList'))} title="Numbered List">
          <ListOrdered size={16} strokeWidth={1.5} />
        </button>
        <button type="button" onClick={() => editor.chain().focus().toggleTaskList().run()} className={btnClass(editor.isActive('taskList'))} title="Checklist">
          <CheckSquare size={16} strokeWidth={1.5} />
        </button>
        <div className="w-px bg-[#E4E4E7] mx-1"></div>
        <button type="button" onClick={() => editor.chain().focus().sinkListItem('listItem').run()} className={btnClass(false)} title="Indent">
          <IndentIncrease size={16} strokeWidth={1.5} />
        </button>
        <button type="button" onClick={() => editor.chain().focus().liftListItem('listItem').run()} className={btnClass(false)} title="Outdent">
          <IndentDecrease size={16} strokeWidth={1.5} />
        </button>
        <div className="w-px bg-[#E4E4E7] mx-1"></div>
        {/* Highlight Picker */}
        <div className="relative" ref={pickerRef}>
          <button
            type="button"
            data-testid="highlight-btn"
            onClick={() => setShowHighlightPicker(!showHighlightPicker)}
            className={btnClass(editor.isActive('highlight'))}
            title="Highlight"
          >
            <Highlighter size={16} strokeWidth={1.5} />
          </button>
          {showHighlightPicker && (
            <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border border-slate-200 p-2 z-50 flex gap-1.5" data-testid="highlight-picker">
              {HIGHLIGHT_COLORS.map((c) => (
                <button
                  key={c.color}
                  type="button"
                  data-testid={`highlight-color-${c.name.toLowerCase()}`}
                  onClick={() => {
                    editor.chain().focus().toggleHighlight({ color: c.color }).run();
                    setShowHighlightPicker(false);
                  }}
                  className="w-7 h-7 rounded-md border-2 transition-all hover:scale-110"
                  style={{ backgroundColor: c.color, borderColor: editor.isActive('highlight', { color: c.color }) ? '#4f46e5' : 'transparent' }}
                  title={c.name}
                />
              ))}
              <button
                type="button"
                data-testid="highlight-remove"
                onClick={() => {
                  editor.chain().focus().unsetHighlight().run();
                  setShowHighlightPicker(false);
                }}
                className="w-7 h-7 rounded-md border-2 border-slate-200 flex items-center justify-center text-slate-400 hover:text-red-500 hover:border-red-300 transition-all text-xs font-bold"
                title="Remove highlight"
              >
                ×
              </button>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => {
            const url = window.prompt('Enter URL:');
            if (url) editor.chain().focus().setLink({ href: url }).run();
          }}
          className={btnClass(editor.isActive('link'))}
          title="Add Link"
        >
          <LinkIcon size={16} strokeWidth={1.5} />
        </button>
        <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className={`p-2 px-3 rounded hover:bg-white transition-colors font-semibold ${editor.isActive('heading', { level: 2 }) ? 'bg-white text-[#FF5A1F]' : 'text-[#71717A]'}`} title="Heading">
          H
        </button>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
};
